import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleGenAI } from '@google/genai';

const SYSTEM_INSTRUCTION = `You are the AI guide for Tuniverse 3D — an immersive, procedurally generated ocean world built with Three.js. Visitors fly over an infinite ocean with dynamic sky, clouds, and underwater environments.

You can control the scene by returning settings alongside your message. Here are the parameters you can adjust:

CLOUDS:
- coverage (0.0–1.0): cloud cover amount. 0 = clear, 1 = overcast
- cloudDensity (0.001–0.5): opacity/thickness of clouds
- windSpeed (0–10): how fast clouds move
- cloudBase (50–10000): lowest altitude of clouds
- cloudTop (100–20000): highest altitude of clouds

SKY:
- azimuth (0–1): sun horizontal position. 0.25 = east, 0.5 = south, 0.75 = west
- inclination (0–0.5): sun height. 0 = horizon, 0.5 = directly overhead
- rayleigh (0–4): atmospheric scattering. Higher = more vivid sky color

OCEAN:
- seaSpeed (0.0–3.0): wave animation speed
- seaHeight (0.0–3.0): wave height
- seaChoppy (0.0–6.0): wave choppiness/roughness
- seaFreq (0.01–1.0): wave frequency
- normalStrength (0.0–2.0): micro-detail ripple intensity on water surface. Higher = more shimmering detail
- sssIntensity (0.0–5.0): subsurface scattering glow on wave crests. Higher = more translucent green-teal glow when backlit
- foamAmount (0.0–2.0): white foam at wave convergence points. Higher = more visible foam lines

MOOD PRESETS you can create by combining settings:
- Stormy: high coverage, high seaChoppy, high seaHeight, low inclination, high rayleigh, high foamAmount (~1.5), high normalStrength (~1.2)
- Calm sunset: low coverage, low seaChoppy, low seaHeight, low inclination (near 0.49), azimuth ~0.3, low foamAmount (~0.2), high sssIntensity (~2.0)
- Clear day: low coverage, high inclination, moderate seaHeight, moderate normalStrength (~0.4)
- Dramatic dawn: azimuth ~0.25, inclination ~0.1, high rayleigh, moderate coverage, high sssIntensity (~2.5)
- Moonlit night: inclination ~0.0, rayleigh ~0.5, low coverage, low sssIntensity (~0.3), low foamAmount (~0.1)

IMPORTANT RULES:
1. Always respond with valid JSON: {"message": "your response text", "settings": {optional parameter changes}}
2. Only include "settings" when you want to change the scene. Omit it for purely conversational responses.
3. Keep messages concise and atmospheric — you're a guide to a mystical ocean world.
4. When users ask what they can do, mention: flying over the ocean (WASD/mouse), exploring underwater, toggling music, and asking you to change the mood/weather.
5. Be creative with scene changes — smoothly transition moods when asked.
6. Never include markdown or code blocks in your message — just plain text.`;

const ai = process.env.REACT_APP_GOOGLE_AI_API_KEY
  ? new GoogleGenAI({ apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY })
  : null;

function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Welcome to Tuniverse. Ask me anything about this world, or tell me a mood and I\'ll reshape the scene for you.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const historyRef = useRef([]);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    if (!ai) {
      setMessages(prev => [...prev, { role: 'user', text }, { role: 'assistant', text: 'API key not configured. Add REACT_APP_GOOGLE_AI_API_KEY to your .env file and restart.' }]);
      setInput('');
      return;
    }

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    historyRef.current.push({ role: 'user', parts: [{ text }] });

    try {
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-lite',
        contents: historyRef.current,
        config: {
          systemInstruction: SYSTEM_INSTRUCTION,
          responseMimeType: 'application/json',
        },
      });

      const raw = response.text;
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = { message: raw };
      }

      const assistantText = parsed.message || raw;
      historyRef.current.push({ role: 'model', parts: [{ text: raw }] });

      setMessages(prev => [...prev, { role: 'assistant', text: assistantText }]);

      if (parsed.settings && typeof parsed.settings === 'object') {
        const cleaned = {};
        for (const [key, value] of Object.entries(parsed.settings)) {
          if (typeof value === 'number') {
            cleaned[key] = value;
          }
        }
        if (Object.keys(cleaned).length > 0) {
          window.dispatchEvent(new CustomEvent('tuniverse-settings', { detail: cleaned }));
        }
      }
    } catch (err) {
      console.error('ChatAssistant error:', err);
      const errMsg = err?.message || '';
      let userMsg = 'Something went wrong. Please try again.';
      if (errMsg.includes('quota') || errMsg.includes('RESOURCE_EXHAUSTED') || errMsg.includes('429')) {
        userMsg = 'API quota exceeded. Please wait a moment and try again.';
      } else if (errMsg.includes('API_KEY_INVALID') || errMsg.includes('401') || errMsg.includes('403')) {
        userMsg = 'API key is invalid. Check your REACT_APP_GOOGLE_AI_API_KEY in .env.';
      }
      setMessages(prev => [...prev, { role: 'assistant', text: userMsg }]);
      historyRef.current.pop();
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat toggle button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        style={{
          position: 'absolute',
          bottom: 32,
          left: 32,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.7)',
          background: open ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          backdropFilter: 'blur(4px)',
          transition: 'border-color 0.3s, background 0.3s',
          zIndex: 100,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 1)';
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.45)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.7)';
          e.currentTarget.style.background = open ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.3)';
        }}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <line x1="4" y1="4" x2="16" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <line x1="16" y1="4" x2="4" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 100,
            left: 32,
            width: 380,
            maxWidth: 'calc(100vw - 64px)',
            height: 480,
            maxHeight: 'calc(100vh - 160px)',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 99,
            animation: 'chatSlideUp 0.3s ease-out',
          }}
        >
          <style>{`
            @keyframes chatSlideUp {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <span style={{ color: 'white', fontSize: 14, fontWeight: 600, letterSpacing: 1 }}>
              TUNIVERSE AI
            </span>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontSize: 18,
                padding: '0 4px',
                lineHeight: 1,
              }}
            >
              x
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <div style={{
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.role === 'user'
                    ? 'rgba(59, 130, 246, 0.4)'
                    : 'rgba(255, 255, 255, 0.08)',
                  color: 'white',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '14px 14px 14px 4px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 13,
                }}>
                  ...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            gap: 8,
            flexShrink: 0,
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: 10,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.06)',
                color: 'white',
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: 'none',
                background: input.trim() ? 'rgba(59, 130, 246, 0.6)' : 'rgba(255, 255, 255, 0.06)',
                cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatAssistant;
